import React, { useMemo, useCallback } from "react";
import {
  CUSTOMER_TYPES_NAMES,
  BASE_URL,
  IsCurrentUser,
  SHORT_DATE_FORMAT,
} from "../../../constants";
import moment from "moment";
import { Progress, Button } from "reactstrap";

/**
 * @typedef {Object} IAccount
 * @property {boolean} isValidNewPassword
 * @property {boolean} canDelete
 * @property {string} userId
 * @property {string} userName
 * @property {string} lastLoginDate
 * @property {string} createDate
 * @property {string} lastActivityDate
 * @property {Roles[]} roles
 * @property {boolean} isLockedOut
 * @property {boolean} isActive
 * @property {string} newPassword
 * @property {string} confirmedNewPassword
 * @property {object} bdmId
 * @property {object} driverId
 * @property {UserContact} userContact
 * @property {AuthorisationRequests[]} authorisationRequests
 * @property {string} externalProvider
 * @property {string} externalProviderId
 * @property {boolean} hasAuthorisationRequest
 * @property {string} contactName
 * @property {any[]} userNotificationCommunications
 * @property {object} currentPassword
 * @property {boolean} forceResetPassword
 * @property {object} passwordResetToken
 * @property {object} isExternalProvider
 */
/**
 * @typedef {Object} Roles
 * @property {string} roleId
 * @property {string} roleName
 */
/**
 * @typedef {Object} UserContact
 * @property {string} position
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} fullName
 * @property {string} updatedDate
 * @property {string} createdDate
 * @property {string} updatedBy
 * @property {any[]} addresses
 * @property {Communications[]} communications
 * @property {any[]} attachments
 * @property {any[]} notes
 * @property {object} invitedByUserName
 * @property {object} invitedDate
 * @property {object} inviteAcceptedDate
 * @property {number} id
 */
/**
 * @typedef {Object} Communications
 * @property {number} id
 * @property {string} communicationType
 * @property {string} detail
 * @property {boolean} isPrimary
 * @property {string} updatedDate
 * @property {string} createdDate
 * @property {string} updatedBy
 * @property {boolean} receiveCompletedServiceNotification
 * @property {boolean} receiveMarketingNotification
 */
/**
 * @typedef {Object} AuthorisationRequests
 * @property {string} requestedByUserName
 * @property {string} toLocationName
 * @property {object} toClientName
 * @property {string} actionedByUserName
 * @property {object} assignedToUserName
 * @property {string} actionTypeDescription
 * @property {object} revokedByUserName
 * @property {boolean} isActive
 * @property {number} id
 * @property {string} requestedByUserId
 * @property {string} createdDate
 * @property {number} toLocationId
 * @property {object} toClientId
 * @property {number} actionType
 * @property {string} actionedByUserId
 * @property {string} actionedDate
 * @property {object} assignedToUserId
 * @property {object} originalRequestInfo
 * @property {string} reason
 * @property {object} revokedByUserId
 * @property {object} revokedReason
 * @property {object} revokedDate
 */

/**
 * @typedef {Object} IUser
 * @property {boolean} isValidNewPassword
 * @property {boolean} canDelete
 * @property {string} userId
 * @property {string} userName
 * @property {string} lastLoginDate
 * @property {string} createDate
 * @property {string} lastActivityDate
 * @property {Roles[]} roles
 * @property {boolean} isLockedOut
 * @property {boolean} isActive
 * @property {string} newPassword
 * @property {string} confirmedNewPassword
 * @property {object} bdmId
 * @property {object} driverId
 * @property {UserContact} userContact
 * @property {AuthorisationRequests[]} authorisationRequests
 * @property {string} externalProvider
 * @property {string} externalProviderId
 * @property {boolean} hasAuthorisationRequest
 * @property {string} contactName
 * @property {any[]} userNotificationCommunications
 * @property {object} currentPassword
 * @property {boolean} forceResetPassword
 * @property {object} passwordResetToken
 * @property {object} isExternalProvider
 */

/**
 * @typedef {Object} ILocation
 * @property {number} id
 * @property {number} daysSinceLastPickup
 * @property {boolean} isUrgentJob
 * @property {number} distance
 * @property {string} locationName
 * @property {boolean} hasTankPickups
 * @property {boolean} hasTankDropoffs
 * @property {boolean} isInClosedPeriod
 * @property {boolean} isClosedOnDeliveryDate
 * @property {number} capacityLitres
 * @property {object?} buildingName
 * @property {string} address
 * @property {object?} nextPickupNote
 * @property {number} tanksCapacityLitres
 * @property {number} forecastRate
 * @property {number} actualForPeriod
 * @property {number} actualLastYear
 * @property {number} estimatedToReach
 * @property {number} estimatedToReachPlus7
 * @property {string} customerTypeCode
 * @property {string} customerTypeName
 * @property {number} tinsExpected
 * @property {number} tinsExpectedPlus7
 * @property {string} estimatedToReachDate
 * @property {number} capacityThreshold
 * @property {string} regionName
 * @property {number} regionId
 * @property {object} pendingRunStartDate
 * @property {boolean} isNonBulk
 * @property {string} companyName
 * @property {string} suburb
 * @property {number} latitude
 * @property {number} longitude
 * @property {string} typeName
 * @property {string} typeCode
 * @property {number} percentageOfCapacity
 * @property {number} percentageOfCapacityPlus7
 * @property {number} pickupCalculatedLitres
 * @property {string} fullAddress
 * @property {PotentialPickup} potentialPickup
 * @property {Runs[]} runs
 * @property {Contacts[]} contacts
 * @property {Communications[]} communications
 * @property {string} requestedByUserName
 * @property {string} toLocationName
 * @property {object?} toClientName
 * @property {string} actionedByUserName
 * @property {object?} assignedToUserName
 * @property {string} actionTypeDescription
 * @property {object?} revokedByUserName
 * @property {boolean} isActive
 * @property {string} requestedByUserId
 * @property {string} createdDate
 * @property {number} toLocationId
 * @property {object?} toClientId
 * @property {number} actionType
 * @property {string} actionedByUserId
 * @property {string} actionedDate
 * @property {object?} assignedToUserId
 * @property {object?} originalRequestInfo
 * @property {string} reason
 * @property {object?} revokedByUserId
 * @property {object?} revokedReason
 * @property {object?} revokedDate
 */
/**
 * @typedef {Object} PotentialPickup
 * @property {any[]} tankRequests
 * @property {number} id
 * @property {number} locationId
 * @property {boolean} isUrgentJob
 * @property {string} locationFullAddress
 * @property {number} locationLatitude
 * @property {number} locationLongitude
 * @property {string} locationTypeCode
 * @property {number} locationImageCount
 * @property {string} clientCompanyName
 * @property {string} locationTypeName
 * @property {boolean} isTinLocation
 * @property {number} calculatedLitres
 * @property {number} expectedTins
 * @property {object} pickupNote
 * @property {LocationCommunications[]} locationCommunications
 * @property {LocationContacts[]} locationContacts
 * @property {string} dateCreated
 * @property {string} pickupStatusCode
 */
/**
 * @typedef {Object} LocationCommunications
 * @property {number} id
 * @property {string} detail
 * @property {string} communicationType
 * @property {number} locationId
 */
/**
 * @typedef {Object} LocationContacts
 * @property {number} id
 * @property {string} fullName
 * @property {number} locationId
 * @property {Communications[]} communications
 */
/**
 * @typedef {Object} Communications
 * @property {number} id
 * @property {string} detail
 * @property {string} communicationType
 * @property {number} locationId
 */
/**
 * @typedef {Object} Runs
 * @property {number} id
 * @property {string} timeStart
 * @property {boolean} isComplete
 * @property {number} locationId
 */
/**
 * @typedef {Object} Contacts
 * @property {number} id
 * @property {string} fullName
 * @property {number} locationId
 * @property {Communications[]} communications
 */

/**
 * @typedef {(ar: {id: number}, reason: any) => void} RevokeRequestCallback
 */

/**
 * @typedef {Object} LocationUsageProps
 * @property {ILocation?} location
 * @property {boolean} showLink
 * @property {boolean} showAuthorisationDetails
 * @property {RevokeRequestCallback} revokeRequest
 * @property {IAccount} account
 * @property {IUser} user
 */

/**
 * @param {LocationUsageProps} props
 * @returns
 */
function LocationUsage({
  location,
  showLink,
  onSelect,
  showAuthorisationDetails,
  user,
  account,
  revokeRequest,
}) {
  if (!location) {
    return null;
  }

  const style = useMemo(() => {
    if (
      !showAuthorisationDetails &&
      (location.daysSinceLastPickup == undefined ||
        location.daysSinceLastPickup == null)
    ) {
      return { border: "solid 1px blue" };
    }
    if (location.isUrgentJob) {
      return { border: "solid 1px red" };
    }
    if (location.pendingRunStartDate) {
      return { border: "solid 1px orange" };
    }
    return {};
  }, [showAuthorisationDetails, location]);

  let round = (value, decimals) => {
    return Number(Math.round(value + "e" + decimals) + "e-" + decimals);
  };

  const isCollectionType = useMemo(() => {
    return location.customerTypeCode === CUSTOMER_TYPES_NAMES.Collection;
  }, [location]);

  const getProgressColor = useCallback(
    (plus7 = false) => {
      let color = "success";
      const {
        percentageOfCapacity,
        percentageOfCapacityPlus7,
      } = location;
      const percentage = plus7
        ? percentageOfCapacityPlus7
        : percentageOfCapacity;
      if (!isCollectionType) {
        if (percentage <= 15) {
          color = "danger";
        } else if (percentage <= 30) {
          color = "warning";
        }
      } else {
        if (percentage >= 85) {
          color = "danger";
        } else if (percentage >= 70) {
          color = "warning";
        }
      }
      return color;
    },
    [location, isCollectionType]
  ); 
  

  return (
    <div className="card" style={style}>
      <div className="card-block">
        <h4 className="card-title">
          {showLink ? (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                showAuthorisationDetails
                  ? console.log("")
                  : onSelect(`${BASE_URL}/manage/locations/${location.id}`);
              }}
            >
              {location.locationName
                ? `${location.customerTypeCode} ${location.customerTypeName} - ${location.locationName}`
                : `${location.toLocationName}`}
            </a>
          ) : location.locationName ? (
            `${location.customerTypeCode} ${location.customerTypeName} - ${location.locationName}`
          ) : (
            `${location.toLocationName}`
          )}
        </h4>
        {!location.revokedDate && (
          <React.Fragment>
            {location.isInClosedPeriod && (
              <div className={"row"}>
                <h5>
                  <div
                    className="text-muted pull-left"
                    style={{ marginLeft: "15px" }}
                  >
                    <i
                      className={"fa fa-exclamation-triangle fa-2 text-warning"}
                    />
                    &nbsp;&nbsp;
                    {location.isInClosedPeriod &&
                      "Location is currently in a closed period"}
                  </div>
                </h5>
              </div>
            )}

            {location.pendingRunStartDate && (
              <h6>
                This location is scheduled for{" "}
                {isCollectionType ? "pickup" : "delivery"} on{" "}
                {moment(location.pendingRunStartDate).format("DD/MM/YYYY")}
              </h6>
            )}
            {location.isNonBulk ? (
                <h5>
                  <div>
                    <div className="text-muted small">
                      {isCollectionType
                          ? "Expected: " + round(location.estimatedToReach, 1)
                          : "Expected to deliver: " + round(location.estimatedToReach, 1)}
                      L on{" "}
                      {moment(location.estimatedToReachDate).format(
                          SHORT_DATE_FORMAT
                      )}
                    </div>
                    <div className="text-muted small">
                      Expected {location.customerTypeName === "Tin" ? "tins" : "BIBs"}
                      : {location.tinsExpected}
                    </div>
                    <div className="text-muted small">
                      Expected {location.customerTypeName === "Tin" ? "tins" : "BIBs"}{" "}
                      + 7: {location.tinsExpectedPlus7}
                    </div>
                    <div className="text-muted small">
                      Days since last: {location.daysSinceLastPickup}
                    </div>
                  </div>
                </h5>
            ) : (
                <h5>
                  <div className="text-right">
                    <div className="text-muted pull-left">
                      {isCollectionType
                          ? "Estimated to reach: " + round(location.estimatedToReach, 1)
                          : "Estimated to deliver: " + round(location.estimatedToReach, 1)}
                      L on{" "}
                      {moment(location.estimatedToReachDate).format(
                          SHORT_DATE_FORMAT
                      )}
                    </div>
                    <div className="text-muted">
                      Max capacity: {location.capacityLitres}L
                    </div>
                  </div>

                  <Progress
                      style={{marginTop: "10px", marginBottom: "10px"}}
                      max={100}
                      value={location.percentageOfCapacity === 0 ? 100 : location.percentageOfCapacity}
                      color={getProgressColor()}                      
                  >
                    { isCollectionType ? <span>Projected {location.percentageOfCapacity}%</span> :
                        <span>Remaining {location.percentageOfCapacity}%</span>
                    }
                  </Progress>

                  <Progress
                      style={{ marginTop: "10px", marginBottom: "10px" }}
                      max={100}
                      value={location.percentageOfCapacityPlus7 === 0 ? 100 : location.percentageOfCapacityPlus7}
                      color={getProgressColor(true)}
                  >
                    { isCollectionType ? <span>Projected + 7 days {location.percentageOfCapacityPlus7}%</span> :
                        <span>Remaining + 7 days {location.percentageOfCapacityPlus7}%</span>
                    }
                  </Progress>
                  <div className="text-muted small">
                    Actual rate: {round(location.actualForPeriod, 2)} L/D
                  </div>
                  <div className="text-muted small">
                    Actual last year: {round(location.actualLastYear, 2)} L/D
                  </div>
                  <div className="text-muted small">
                    Forecast: {round(location.forecastRate, 2)} L/D
                  </div>
                  <div className="text-muted small">
                    Days since last: {location.daysSinceLastPickup} days
                  </div>
                </h5>
            )}
          </React.Fragment>
        )}

        {showAuthorisationDetails && (
            <h5>
              <div className="text-muted small">
                Actioned By:{" "}
                {location.actionedByUserName ? location.actionedByUserName : ""}
              </div>
              <div className="text-muted small">
                Actioned Date:{" "}
                {location.actionedDate
                    ? moment(location.actionedDate).format(SHORT_DATE_FORMAT)
                    : ""}
              </div>
              {location.revokedDate ? (
                  <React.Fragment>
                    <div className="text-muted small">
                      Revoked Date:{" "}
                      {location.revokedDate
                          ? moment(location.revokedDate).format(SHORT_DATE_FORMAT)
                          : ""}
                    </div>
                    <div className="text-muted small">
                      Revoked Reason:{" "}
                      {location.revokedReason ? location.revokedReason : ""}
                    </div>
                  </React.Fragment>
              ) : (
                  !IsCurrentUser(account, user) &&
                  location &&
                  location.isActive &&
                  location.actionType === 1 && (
                      <div>
                        <Button
                            color="danger"
                            onClick={() => revokeRequest(location)}
                            style={{marginTop: "10px"}}
                        >
                          <i className="fa fa-times"/>
                          &nbsp;Revoke
                        </Button>
                      </div>
                  )
              )}
            </h5>
        )}
      </div>
    </div>
  );
}

export default LocationUsage;
